import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Sparkles, Loader2, Copy, Mail, MessageCircle, Check } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

/** Generic "Suggerimenti AI" dialog for materials/times/manpower */
export const SuggerimentiDialog = ({ open, onOpenChange, categoria, descrizione, metratura }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    React.useEffect(() => {
        if (open && !data) {
            (async () => {
                setLoading(true);
                try {
                    const res = await api.post('/ai/suggerimenti', { categoria, descrizione, metratura });
                    setData(res.data);
                } catch (e) {
                    toast.error('Errore AI');
                } finally { setLoading(false); }
            })();
        }
        if (!open) setData(null);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> SUGGERIMENTI AI</DialogTitle></DialogHeader>
                {loading && <div className="py-12 text-center"><Loader2 className="w-10 h-10 mx-auto animate-spin text-[hsl(var(--accent))]" /><p className="text-sm text-[hsl(var(--muted-foreground))] mt-3">Analizzo materiali e tempi…</p></div>}
                {data && (
                    <div className="space-y-5 mt-2">
                        <div>
                            <h4 className="font-display text-lg mb-2">MATERIALI</h4>
                            <table className="w-full text-sm">
                                <thead className="bg-[hsl(var(--muted))]"><tr><th className="p-2 text-left">Materiale</th><th className="p-2">Unità</th><th className="p-2 text-right">€/u</th></tr></thead>
                                <tbody>
                                    {(data.materiali || []).map((m, i) => <tr key={i} className="border-t"><td className="p-2">{m.nome}{m.note && <span className="text-xs text-[hsl(var(--muted-foreground))] block">{m.note}</span>}</td><td className="p-2 text-center text-xs">{m.unita}</td><td className="p-2 text-right tabular-nums">€{m.prezzo_unitario_eur}</td></tr>)}
                                </tbody>
                            </table>
                        </div>
                        <div>
                            <h4 className="font-display text-lg mb-2">MANODOPERA</h4>
                            <table className="w-full text-sm">
                                <thead className="bg-[hsl(var(--muted))]"><tr><th className="p-2 text-left">Ruolo</th><th className="p-2 text-right">Ore</th><th className="p-2 text-right">€/h</th></tr></thead>
                                <tbody>
                                    {(data.manodopera || []).map((m, i) => <tr key={i} className="border-t"><td className="p-2">{m.ruolo}</td><td className="p-2 text-right tabular-nums">{m.ore_stimate}</td><td className="p-2 text-right tabular-nums">€{m.tariffa_oraria_eur}</td></tr>)}
                                </tbody>
                            </table>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-[hsl(var(--muted))]">
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">TEMPI STIMATI</div>
                                <div className="font-display text-3xl">{data.tempi?.giorni_lavorativi} <span className="text-lg">gg lavorativi</span></div>
                                <ul className="text-xs text-[hsl(var(--muted-foreground))] mt-2 space-y-0.5">
                                    {(data.tempi?.fattori_che_influenzano || []).map((f, i) => <li key={i}>• {f}</li>)}
                                </ul>
                            </div>
                            <div className="p-4 rounded-lg bg-[hsl(var(--muted))]">
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">ATTREZZATURE</div>
                                <ul className="text-sm mt-2 space-y-1">
                                    {(data.checklist_attrezzature || []).map((a, i) => <li key={i}>• {a}</li>)}
                                </ul>
                            </div>
                        </div>
                        {data.rischi_costo?.length > 0 && (
                            <div className="p-4 rounded-lg border-l-4 border-[hsl(0_72%_52%)] bg-[hsl(0_80%_98%)]">
                                <div className="font-semibold text-sm mb-1 text-[hsl(0_72%_42%)]">⚠️ RISCHI DI COSTO</div>
                                <ul className="text-sm space-y-1">{(data.rischi_costo || []).map((r, i) => <li key={i}>• {r}</li>)}</ul>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};


/** Dialog: AI-generated work description from brief input. */
export const DescrizioneAIDialog = ({ open, onOpenChange, onApply }) => {
    const [form, setForm] = useState({ categoria: 'Idraulica', breve_descrizione: '', metratura: '' });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const generate = async () => {
        if (!form.breve_descrizione.trim()) { toast.error('Inserisci una descrizione breve'); return; }
        setLoading(true);
        try {
            const res = await api.post('/ai/descrizione-lavori', {
                ...form,
                metratura: form.metratura ? parseFloat(form.metratura) : null,
            });
            setResult(res.data);
        } catch (e) { toast.error('Errore AI'); }
        finally { setLoading(false); }
    };

    const apply = () => {
        const text = `${result.descrizione_estesa}\n\nLavorazioni:\n${result.lavorazioni.map(l => `- ${l}`).join('\n')}\n\nEsclusioni:\n${(result.esclusioni_tipiche||[]).map(l => `- ${l}`).join('\n')}`;
        onApply?.(text, result.titolo);
        onOpenChange(false);
        setResult(null);
        setForm({ categoria: 'Idraulica', breve_descrizione: '', metratura: '' });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> DESCRIZIONE AI</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Scrivi in poche parole il lavoro, l'AI lo trasforma in una descrizione professionale.</p>
                    <div className="grid grid-cols-3 gap-2">
                        <Select value={form.categoria} onValueChange={(v) => setForm({...form, categoria: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{['Idraulica','Elettricità','Muratura','Tinteggiatura','Piastrelle','Ristrutturazione bagno','Ristrutturazione cucina','Coperture','Cartongesso','Altro'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" placeholder="mq" value={form.metratura} onChange={(e) => setForm({...form, metratura: e.target.value})} className="col-span-2" />
                    </div>
                    <Textarea rows={3} placeholder='Es. "rifacimento bagno completo, vasca con doccia"' value={form.breve_descrizione} onChange={(e) => setForm({...form, breve_descrizione: e.target.value})} data-testid="ai-desc-input" />
                    <Button onClick={generate} disabled={loading} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="ai-desc-generate">
                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generazione…</> : <><Sparkles className="w-4 h-4 mr-2" /> Genera descrizione</>}
                    </Button>

                    {result && (
                        <div className="mt-4 space-y-3 p-4 rounded-lg bg-[hsl(var(--muted))]">
                            <div><Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] mb-1">TITOLO</Badge><p className="font-semibold">{result.titolo}</p></div>
                            <div><div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">DESCRIZIONE PROFESSIONALE</div><p className="text-sm">{result.descrizione_estesa}</p></div>
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">LAVORAZIONI</div>
                                <ul className="text-sm space-y-0.5">{result.lavorazioni?.map((l, i) => <li key={i}>• {l}</li>)}</ul>
                            </div>
                            {result.esclusioni_tipiche?.length > 0 && <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">ESCLUSIONI</div>
                                <ul className="text-sm space-y-0.5">{result.esclusioni_tipiche.map((l, i) => <li key={i}>• {l}</li>)}</ul>
                            </div>}
                            <Button onClick={apply} className="w-full bg-[hsl(220_13%_10%)] text-white" data-testid="ai-desc-apply"><Check className="w-4 h-4 mr-2" /> Usa questa descrizione</Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};


/** Dialog: AI summary of a cantiere with copy buttons. */
export const RiepilogoCantiereDialog = ({ open, onOpenChange, cantiereId, cantiereName }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    React.useEffect(() => {
        if (open && cantiereId && !data) {
            (async () => {
                setLoading(true);
                try {
                    const res = await api.post(`/ai/riepilogo-cantiere/${cantiereId}`);
                    setData(res.data);
                } catch (e) { toast.error('Errore AI'); }
                finally { setLoading(false); }
            })();
        }
        if (!open) setData(null);
    }, [open, cantiereId]);

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copiato!'); };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> RIEPILOGO AI · {cantiereName}</DialogTitle></DialogHeader>
                {loading && <div className="py-12 text-center"><Loader2 className="w-10 h-10 mx-auto animate-spin text-[hsl(var(--accent))]" /></div>}
                {data && (
                    <div className="space-y-4 mt-2">
                        <div className="p-4 rounded-lg bg-[hsl(var(--muted))]">
                            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">STATO</div>
                            <p className="text-sm font-medium">{data.stato_sintetico}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            <div className="p-3 rounded bg-[hsl(145_45%_94%)]"><div className="text-xs text-[hsl(145_55%_34%)] mb-1">COMPLETATE</div><ul className="text-xs">{(data.fasi_completate || []).map((f, i) => <li key={i}>✓ {f}</li>)}</ul></div>
                            <div className="p-3 rounded bg-[hsl(210_90%_94%)]"><div className="text-xs text-[hsl(210_90%_40%)] mb-1">IN CORSO</div><ul className="text-xs">{(data.fasi_in_corso || []).map((f, i) => <li key={i}>○ {f}</li>)}</ul></div>
                            <div className="p-3 rounded bg-[hsl(var(--muted))]"><div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">DA INIZIARE</div><ul className="text-xs">{(data.fasi_da_iniziare || []).map((f, i) => <li key={i}>· {f}</li>)}</ul></div>
                        </div>
                        {data.punti_attenzione?.length > 0 && <div className="p-3 rounded-lg border-l-4 border-[hsl(45_100%_52%)] bg-[hsl(45_100%_98%)]">
                            <div className="text-xs font-semibold mb-1">⚠ PUNTI DI ATTENZIONE</div>
                            <ul className="text-sm">{data.punti_attenzione.map((p, i) => <li key={i}>• {p}</li>)}</ul>
                        </div>}
                        <div className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">MESSAGGIO PRONTO PER IL CLIENTE</div>
                                <Button size="sm" variant="ghost" onClick={() => copy(data.messaggio_per_cliente)} className="gap-1" data-testid="copy-cliente-msg"><Copy className="w-3 h-3" /> Copia</Button>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{data.messaggio_per_cliente}</p>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};


/** Dialog: AI follow-up generator for fattura/preventivo with email + whatsapp output. */
export const FollowUpDialog = ({ open, onOpenChange, tipo, riferimentoId, riferimentoLabel }) => {
    const [tono, setTono] = useState('cortese');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const generate = async () => {
        setLoading(true);
        try {
            const res = await api.post('/ai/follow-up', { tipo, riferimento_id: riferimentoId, tono });
            setData(res.data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Errore AI'); }
        finally { setLoading(false); }
    };

    React.useEffect(() => { if (!open) setData(null); }, [open]);
    const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copiato!'); };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> FOLLOW-UP AI</DialogTitle></DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Genera un messaggio di follow-up per {riferimentoLabel}.</p>
                <div className="flex gap-3 items-end mt-3">
                    <div className="flex-1">
                        <Label>Tono</Label>
                        <Select value={tono} onValueChange={setTono}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cortese">Cortese (primo contatto)</SelectItem>
                                <SelectItem value="sollecito">Sollecito (ricordo)</SelectItem>
                                <SelectItem value="molto_sollecito">Molto sollecito (ultimo avviso)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={generate} disabled={loading} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="ai-followup-generate">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Genera</>}
                    </Button>
                </div>
                {data && (
                    <Tabs defaultValue="email" className="mt-4">
                        <TabsList>
                            <TabsTrigger value="email"><Mail className="w-4 h-4 mr-1" /> Email</TabsTrigger>
                            <TabsTrigger value="whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</TabsTrigger>
                        </TabsList>
                        <TabsContent value="email">
                            <div className="border rounded-lg overflow-hidden">
                                <div className="bg-[hsl(var(--muted))] p-3 border-b text-sm">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Oggetto</div>
                                    <div className="font-semibold flex items-center justify-between gap-2">{data.oggetto_email} <Button size="sm" variant="ghost" onClick={() => copy(data.oggetto_email)}><Copy className="w-3 h-3" /></Button></div>
                                </div>
                                <div className="p-3">
                                    <div className="flex justify-end mb-1"><Button size="sm" variant="ghost" onClick={() => copy(data.email)} className="gap-1"><Copy className="w-3 h-3" /> Copia testo</Button></div>
                                    <pre className="text-sm whitespace-pre-wrap font-sans">{data.email}</pre>
                                </div>
                            </div>
                        </TabsContent>
                        <TabsContent value="whatsapp">
                            <div className="border rounded-lg p-4 bg-[hsl(145_25%_96%)]">
                                <div className="flex justify-end mb-1"><Button size="sm" variant="ghost" onClick={() => copy(data.whatsapp)} className="gap-1"><Copy className="w-3 h-3" /> Copia</Button></div>
                                <p className="text-sm whitespace-pre-wrap">{data.whatsapp}</p>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    );
};
